import {
  AccountSettings,
  Action,
  AlertSettingsView,
  type ApprovalChallenge,
  type ApprovalProof,
  Channel,
  ChannelTestView,
  CheckInEdit,
  CheckInView,
  CliGrantView,
  CliReportView,
  FeedItem as FeedItemSchema,
  HomeView,
  HookSource,
  MeView,
  Person,
  PlanView,
  PolicyPreset,
  PolicyRule,
  Problem,
  ProblemView as ProblemViewSchema,
  ProjectState as ProjectStateSchema,
  ProjectStatusView,
  ProjectView,
  ServiceActivityView,
  ServicesView,
  TimelineItem as TimelineItemSchema,
  WeeklyView,
} from "@pagerless/schema";
import { z } from "zod";

/**
 * Typed client for cloud/api. Every response is validated with zod before the UI sees it:
 * wire types come from @pagerless/schema; the few web-only view shapes are defined below.
 *
 * Routes (all JSON, cookie session, same origin through the /v1 proxy):
 *   GET  /v1/me                              → Me
 *   POST /v1/auth/email                      { email } → { sent }
 *   GET  /v1/auth/oauth/:provider/start      (browser redirect, not fetched)
 *   POST /v1/auth/passkey/login/options      → PublicKeyCredentialRequestOptionsJSON
 *   POST /v1/auth/passkey/login/verify       { response } → { ok }
 *   POST /v1/auth/passkey/register/options   → PublicKeyCredentialCreationOptionsJSON
 *   POST /v1/auth/passkey/register/verify    { response } → { ok }
 *   POST /v1/auth/logout                     → { ok }
 *   GET  /v1/home                            → Home
 *   GET  /v1/reports/weekly                  → Weekly
 *   GET  /v1/projects                        → { projects }
 *   POST /v1/setup/connect                   → { project, key }
 *   GET  /v1/projects/:id/status             → ProjectStatus
 *   GET  /v1/projects/:id/checkins           → { checkins } (scheduled jobs)
 *   PUT  /v1/projects/:id/checkins/:slug     CheckInEdit → { checkin } (owner only)
 *   GET  /v1/projects/:id/services           → ServicesView (failing first)
 *   GET  /v1/projects/:id/services/:name/activity?limit= → ServiceActivityView (newest first)
 *   POST /v1/projects/:id/keys               → { key, id } (owner only; key shown once)
 *   PUT  /v1/projects/:id/keys/:keyId/hook-secrets/:tool  { secret } → { source, saved } (owner only)
 *   GET  /v1/problems?state=                 → { problems }
 *   GET  /v1/problems/:id                    → ProblemView
 *   POST /v1/problems/:id/ack                → { problem }
 *   POST /v1/actions/:id/challenge           → ApprovalChallenge
 *   POST /v1/actions/:id/approve             { proof } → { action }
 *   POST /v1/actions/:id/reject              → { action }
 *   GET  /v1/rules                           → { rules }
 *   PUT  /v1/rules                           { rule } → { rules }
 *   POST /v1/rules/preset                    { preset } → { rules }
 *   GET  /v1/people                          → { people }
 *   POST /v1/people/invite                   { name, email } → { person }
 *   GET  /v1/channels                        → { channels }
 *   PUT  /v1/channels                        { channel } → { channel }
 *   POST /v1/channels/:id/test               → { ok, at, error? }
 *   GET  /v1/alerts                          → AlertSettings
 *   GET  /v1/plan                            → PlanInfo
 *   POST /v1/plan/checkout                   → { url }
 *   GET  /v1/settings                        → { timeZone }
 *   PUT  /v1/settings                        { timeZone } → { timeZone } (owner only)
 * Errors: non-2xx with { error: { code, message } }; message is plain words safe to show.
 */

// View shapes live in the shared contract (packages/schema/src/views.ts); local names kept for the UI.
export const Me = MeView;
export type Me = MeView;
export const FeedItem = FeedItemSchema;
export type FeedItem = FeedItemSchema;
export const Home = HomeView;
export type Home = HomeView;
export const Weekly = WeeklyView;
export type Weekly = WeeklyView;
export const ProjectState = ProjectStateSchema;
export const Project = ProjectView;
export type Project = ProjectView;
export const ProjectStatus = ProjectStatusView;
export type ProjectStatus = ProjectStatusView;
export const TimelineItem = TimelineItemSchema;
export type TimelineItem = TimelineItemSchema;
export const ProblemView = ProblemViewSchema;
export type ProblemView = ProblemViewSchema;
export const AlertSettings = AlertSettingsView;
export type AlertSettings = AlertSettingsView;
export const PlanInfo = PlanView;
export type PlanInfo = PlanView;

const TestResult = ChannelTestView;
export type TestResult = ChannelTestView;

const ApprovalChallengeSchema = z.object({
  actionId: z.string(),
  challenge: z.string().min(16),
  expiresAt: z.number().int(),
});

/** WebAuthn option JSON is checked by @simplewebauthn/browser; we only make sure it is an object with a challenge. */
const WebAuthnOptions = z.looseObject({ challenge: z.string() });

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    options?: { cause?: unknown },
  ) {
    super(message, options);
    this.name = "ApiError";
  }
}

const ErrorBody = z.object({ error: z.object({ code: z.string(), message: z.string() }) });

/** Only same-site paths, so a `next` from a reply can never send someone off-site. */
export function safePath(next: string): string {
  return next.startsWith("/") && !next.startsWith("//") && !next.includes("\\") ? next : "/home";
}

export type ApiOptions = { fetch: typeof fetch; base?: string };

export function createApi({ fetch: doFetch, base = "" }: ApiOptions) {
  async function call<S extends z.ZodType>(
    method: "GET" | "POST" | "PUT",
    path: string,
    schema: S,
    body?: unknown,
  ): Promise<z.infer<S>> {
    let res: Response;
    try {
      res = await doFetch(`${base}${path}`, {
        method,
        credentials: "same-origin",
        headers: body === undefined ? { accept: "application/json" } : { "content-type": "application/json" },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch (cause) {
      throw new ApiError(0, "network", "We couldn't reach Pagerless. Check your connection and try again.", {
        cause,
      });
    }
    const text = await res.text();
    let json: unknown = null;
    if (text) {
      try {
        json = JSON.parse(text);
      } catch {
        throw new ApiError(res.status, "bad_response", "Pagerless sent a reply we couldn't read. Try again.");
      }
    }
    if (!res.ok) {
      const err = ErrorBody.safeParse(json);
      if (err.success) throw new ApiError(res.status, err.data.error.code, err.data.error.message);
      throw new ApiError(res.status, `http_${res.status}`, "Something went wrong on our side. Try again.");
    }
    const parsed = schema.safeParse(json);
    if (!parsed.success) {
      throw new ApiError(res.status, "bad_response", "Pagerless sent a reply we couldn't read. Try again.");
    }
    return parsed.data;
  }

  const enc = encodeURIComponent;

  return {
    me: () => call("GET", "/v1/me", Me),
    sendEmailLink: (email: string, next?: string) =>
      call("POST", "/v1/auth/email", z.object({ sent: z.boolean() }), next ? { email, next } : { email }),
    /** Uses an emailed sign-in link's token (single use). The api sets the session cookie. */
    verifyEmailLink: (token: string) =>
      call("POST", "/v1/auth/email/verify", z.object({ next: z.string() }), { token }).then((r) => ({
        next: safePath(r.next),
      })),
    oauthStartUrl: (provider: "google" | "github") => `${base}/v1/auth/oauth/${provider}/start`,
    /** Sign-in providers this server is set up for; the pages show only those buttons. */
    oauthProviders: () =>
      call("GET", "/v1/auth/oauth/providers", z.object({ providers: z.array(z.enum(["google", "github"])) })).then(
        (r) => r.providers,
      ),
    passkeyLoginOptions: () => call("POST", "/v1/auth/passkey/login/options", WebAuthnOptions, {}),
    passkeyLoginVerify: (response: unknown) =>
      call("POST", "/v1/auth/passkey/login/verify", z.object({ ok: z.boolean() }), { response }),
    passkeyRegisterOptions: () => call("POST", "/v1/auth/passkey/register/options", WebAuthnOptions, {}),
    passkeyRegisterVerify: (response: unknown) =>
      call("POST", "/v1/auth/passkey/register/verify", z.object({ ok: z.boolean() }), { response }),
    logout: () => call("POST", "/v1/auth/logout", z.object({ ok: z.boolean() }), {}),

    home: () => call("GET", "/v1/home", Home),
    weekly: () => call("GET", "/v1/reports/weekly", Weekly),

    projects: () => call("GET", "/v1/projects", z.object({ projects: z.array(Project) })).then((r) => r.projects),
    connect: () => call("POST", "/v1/setup/connect", z.object({ project: Project, key: z.string().min(8) }), {}),
    projectStatus: (id: string) => call("GET", `/v1/projects/${enc(id)}/status`, ProjectStatus),
    checkIns: (projectId: string) =>
      call("GET", `/v1/projects/${enc(projectId)}/checkins`, z.object({ checkins: z.array(CheckInView) })).then(
        (r) => r.checkins,
      ),
    saveCheckIn: async (projectId: string, slug: string, edit: CheckInEdit) =>
      call(
        "PUT",
        `/v1/projects/${enc(projectId)}/checkins/${enc(slug)}`,
        z.object({ checkin: CheckInView }),
        CheckInEdit.parse(edit),
      ).then((r) => r.checkin),
    /** Every service of an app: what it runs on, its status, what watches it. */
    services: (projectId: string) => call("GET", `/v1/projects/${enc(projectId)}/services`, ServicesView),
    /** The signals one service sent, newest first. `limit` is capped by the server. */
    serviceActivity: (projectId: string, name: string, limit?: number) =>
      call(
        "GET",
        `/v1/projects/${enc(projectId)}/services/${enc(name)}/activity${limit ? `?limit=${limit}` : ""}`,
        ServiceActivityView,
      ),
    /** A new ingest key for an app; the raw key is returned once. */
    createKey: (projectId: string) =>
      call("POST", `/v1/projects/${enc(projectId)}/keys`, z.object({ key: z.string().min(8), id: z.string() }), {}),
    /** Saves a tool's webhook signing secret on a key (write-only). */
    saveHookSecret: (projectId: string, keyId: string, source: HookSource, secret: string) =>
      call(
        "PUT",
        `/v1/projects/${enc(projectId)}/keys/${enc(keyId)}/hook-secrets/${enc(source)}`,
        z.object({ source: HookSource, saved: z.boolean() }),
        { secret },
      ),
    hostConnectUrl: (host: string, projectId: string) =>
      `${base}/v1/connections/${enc(host)}/start?project=${enc(projectId)}`,

    problems: (state?: Problem["state"]) =>
      call("GET", `/v1/problems${state ? `?state=${enc(state)}` : ""}`, z.object({ problems: z.array(Problem) })).then(
        (r) => r.problems,
      ),
    problem: (id: string) => call("GET", `/v1/problems/${enc(id)}`, ProblemView),
    ack: (id: string) =>
      call("POST", `/v1/problems/${enc(id)}/ack`, z.object({ problem: Problem }), {}).then((r) => r.problem),

    requestApproval: (actionId: string): Promise<ApprovalChallenge> =>
      call("POST", `/v1/actions/${enc(actionId)}/challenge`, ApprovalChallengeSchema, {}),
    approve: (actionId: string, proof: ApprovalProof) =>
      call("POST", `/v1/actions/${enc(actionId)}/approve`, z.object({ action: Action }), { proof }).then(
        (r) => r.action,
      ),
    reject: (actionId: string) =>
      call("POST", `/v1/actions/${enc(actionId)}/reject`, z.object({ action: Action }), {}).then((r) => r.action),

    rules: () => call("GET", "/v1/rules", z.object({ rules: z.array(PolicyRule) })).then((r) => r.rules),
    setRule: async (rule: PolicyRule) =>
      call("PUT", "/v1/rules", z.object({ rules: z.array(PolicyRule) }), { rule: PolicyRule.parse(rule) }).then(
        (r) => r.rules,
      ),
    applyPreset: async (preset: PolicyPreset) =>
      call("POST", "/v1/rules/preset", z.object({ rules: z.array(PolicyRule) }), {
        preset: PolicyPreset.parse(preset),
      }).then((r) => r.rules),

    people: () => call("GET", "/v1/people", z.object({ people: z.array(Person) })).then((r) => r.people),
    invite: (name: string, email: string) =>
      call("POST", "/v1/people/invite", z.object({ person: Person }), { name, email }).then((r) => r.person),
    channels: () => call("GET", "/v1/channels", z.object({ channels: z.array(Channel) })).then((r) => r.channels),
    saveChannel: async (channel: Channel) =>
      call("PUT", "/v1/channels", z.object({ channel: Channel }), { channel: Channel.parse(channel) }).then(
        (r) => r.channel,
      ),
    testChannel: (id: string) => call("POST", `/v1/channels/${enc(id)}/test`, TestResult, {}),

    alerts: () => call("GET", "/v1/alerts", AlertSettings),
    plan: () => call("GET", "/v1/plan", PlanInfo),
    checkout: () => call("POST", "/v1/plan/checkout", z.object({ url: z.string().url() }), {}),

    /** `npx pagerless connect`: the terminal waiting behind a code, and the owner's answer. */
    cliGrant: (code: string) => call("GET", `/v1/cli/authorize/${enc(code)}`, CliGrantView),
    cliOptions: (code: string) => call("POST", `/v1/cli/authorize/${enc(code)}/options`, WebAuthnOptions, {}),
    cliApprove: (code: string, response: unknown) =>
      call("POST", `/v1/cli/authorize/${enc(code)}/approve`, z.object({ ok: z.boolean() }), { response }),
    cliDeny: (code: string) => call("POST", `/v1/cli/authorize/${enc(code)}/deny`, z.object({ ok: z.boolean() }), {}),
    /** What the terminal last connected (the setup screen shows it). */
    cliReport: () =>
      call("GET", "/v1/cli/report", z.object({ report: CliReportView.nullable() })).then((r) => r.report),

    settings: () => call("GET", "/v1/settings", AccountSettings),
    setTimeZone: (timeZone: string) => call("PUT", "/v1/settings", AccountSettings, { timeZone }),
  };
}

export type Api = ReturnType<typeof createApi>;
